import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { updateAddress, clearUpdateAddress } from '../../actions/addresses';
import { fetchUser } from '../../actions';
import ErrorAlertContainer from '../ErrorAlertContainer';
import IndicatesRequiredField from '../IndicatesRequiredField';
import AddressInputFields from '../AddressInputFields';
import StyledModal from '../StyledModal';
import { isEmpty } from '../../utils/isEmpty';
import { isValidUSZip } from '../../utils/address';

function validate(formData) {
  const errors = {};

  if (isEmpty(formData.street)) {
    errors.street = 'Street address is required';
  }

  if (isEmpty(formData.city)) {
    errors.city = 'City is required';
  }

  if (isEmpty(formData.state)) {
    errors.state = 'State is required';
  }

  if (isEmpty(formData.zip)) {
    errors.zip = 'ZIP Code is required';
  } else if (!isValidUSZip(formData.zip)) {
    errors.zip = 'Invalid ZIP Code';
  }

  return errors;
}

function initialFormData({
  street = '',
  street1 = '',
  street2 = '',
  city = '',
  state = '',
  zip = '',
}) {
  return {
    city,
    state,
    street: street || street1,
    street2,
    zip,
  };
}

const UpdateAddressModal = ({
  address = {},
  closeModal,
  isOpen,
  openSuccessModal,
}) => {
  const dispatch = useDispatch();
  const addresses = useSelector((state) => state.addresses);

  const [errors, setErrors] = useState(false);
  const [formData, setFormData] = useState(initialFormData(address));

  const handleInputChange = (changedFields) => {
    setFormData({ ...formData, ...changedFields });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const newErrors = validate(formData);
      setErrors(newErrors);

      if (!Object.keys(newErrors).length) {
        dispatch(updateAddress(formData));
      }
    },
    [dispatch, formData, setErrors],
  );

  useEffect(() => {
    // when successful response: close modal, refresh data, and open success modal
    if (addresses.isUpdateSuccess) {
      dispatch(fetchUser());
      dispatch(clearUpdateAddress());
      closeModal();
      openSuccessModal();
    }
  }, [addresses, dispatch, closeModal, openSuccessModal]);

  const submitting = addresses.isFetching;

  return (
    <StyledModal
      isOpen={isOpen}
      closeModal={() => {
        setFormData(initialFormData({}));
        closeModal();
      }}
      submitting={submitting}
      title="Edit your personal address"
      primaryButton={{
        hidden: false,
        text: 'Save changes',
        submittingText: 'Saving...',
      }}
      wrapper={(children) => (
        <form onSubmit={handleSubmit} noValidate={true}>
          {children}
        </form>
      )}
    >
      {isOpen && (
        <>
          <p>The address must be associated with the name on this wallet.</p>
          <IndicatesRequiredField />
          <AddressInputFields
            errors={errors}
            initialFormData={formData}
            onChange={handleInputChange}
          />
          <ErrorAlertContainer />
        </>
      )}
    </StyledModal>
  );
};

UpdateAddressModal.propTypes = {
  address: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  openSuccessModal: PropTypes.func.isRequired,
};

export default UpdateAddressModal;
